import React, { FunctionComponent, useEffect, useState } from 'react'
import { useT, T } from '@transifex/react'
import { Button, Card, Icon, Text } from '@lightspeed/design-system-react'
import { CustomerBadge } from '@vend/react-utilities'
import { ApiCustomer } from '@vend/types'
import { trackUserEvent } from '@vend/utilities'
import { PAYMENT_TYPES } from '@vend/business-components'
import {
  useCustomerManager,
  useRetailerSettingsManager,
} from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import {
  selectSaleCustomer,
  selectOutstandingBalance,
  selectCurrentSale,
  selectSaleLocalId,
  selectSalePayments,
} from '../../../state/slices/currentSale/currentSaleSlice'
import {
  isAccountBalancePaymentAdded,
  isAccountBalancePaymentPaid,
  isBlindReturn,
  isReturn,
  isValidCustomer,
} from '../../../functions/sale'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { PartialCustomerGroup } from '../../react/modals/common/types'
import { CustomerSearch } from '../../customer/customer-search'
import { RemoveCustomerButton } from '../sale-customer-search/RemoveCustomerButton'
import { LockedCustomerIcon } from '../sale-customer-search/LockedCustomerIcon'
import { SaleStatus } from '../../../models/sale.constants'
import { AccountBalanceSetupModal } from '../account-balance-setup-modal/AccountBalanceSetupModal'
import { UpdateSaleCustomerModal } from '../../../modals/update-customer-modal'
import { WEB_REGISTER } from '../../web-register.constant'
import { CustomerBalancesSection } from './CustomerBalancesSection'
import { AddCustomerPopover } from './AddCustomerPopover'
import { isAbpPaymentUiFlowActive } from './isAbpPaymentUiFlowActive'

interface Props {
  canAddAccountBalancePayment?: boolean
  onSelect: (customer?: ApiCustomer) => void | Promise<unknown>
}

export const CustomerSection: FunctionComponent<Props> = ({
  canAddAccountBalancePayment,
  onSelect,
}) => {
  const t = useT()
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const validCustomer = isValidCustomer(saleCustomer)
  const saleId = useAppSelector(selectSaleLocalId)
  const salePayments = useAppSelector(selectSalePayments)
  const lspayPaymentId = salePayments.find(
    payment => payment.paymentType.type_id === PAYMENT_TYPES.LSPAY && payment.id
  )?.id
  const customerPaymentMetadata = lspayPaymentId
    ? { paymentId: lspayPaymentId, paymentStatus: 'after_payment' }
    : { paymentStatus: 'before_payment' }
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const retailerSettingsManager = useRetailerSettingsManager()
  const customerManager = useCustomerManager()
  const getCustomerGroup = customerManager.getCustomerGroup
  const sale = useAppSelector(selectCurrentSale)

  const [showAccountBalanceModal, setShowAccountBalanceModal] = useState(false)
  const [customerSearchQuery, setCustomerSearchQuery] = useState('')
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false)

  const [customerGroup, setCustomerGroup] = useState<
    PartialCustomerGroup | undefined
  >()
  const shouldShowCustomerGroup = getFeatureFromCache('customer_groups')

  useEffect(() => {
    if (shouldShowCustomerGroup && saleCustomer) {
      getCustomerGroup(saleCustomer)
        .then(group => {
          setCustomerGroup(group || undefined)
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.warn('Failed to get customer group', error)
        })
    }
  }, [saleCustomer, shouldShowCustomerGroup, getCustomerGroup])

  const getAddCustomerPlaceholder = () => {
    let placeholder = t('Add a customer to this sale', {
      _context: 'Payment panel customer search placeholder',
    })

    if (
      outstandingBalance > 0 ||
      retailerSettingsManager.isStoreCreditEnabled()
    ) {
      placeholder = t('Add a customer to pay with the following options:', {
        _context: 'Payment panel customer search placeholder',
      })
    }
    return placeholder
  }

  const showAccountBalancePaymentBanner = () => {
    // In the account balance payment flow the Pay input controls the ABP amount
    // directly — the banner and its "Edit or remove" link are redundant and confusing.
    if (isAbpPaymentUiFlowActive(sale.payAccountBalance)) return false

    return Boolean(
      canAddAccountBalancePayment &&
        sale.status !== SaleStatus.ACCOUNT_SALE &&
        sale.status !== SaleStatus.LAYBY_SALE &&
        !isReturn(sale) &&
        !isBlindReturn(sale) &&
        sale.customerBalances?.account &&
        sale.customerBalances.account < 0
    )
  }

  const hasAccountBalancePayment = () => {
    return isAccountBalancePaymentAdded(sale.payAccountBalance)
  }

  const hasPaidAccountBalancePayment = () => {
    return isAccountBalancePaymentPaid(sale.payAccountBalance)
  }

  const getBannerText = () => {
    if (!hasAccountBalancePayment()) {
      return (
        <T
          _str="This customer has an unpaid account balance."
          _context="Customer account balance banner text"
        />
      )
    }
    if (!hasPaidAccountBalancePayment()) {
      return (
        <T
          _str="An account balance payment has been added."
          _context="Customer account balance banner text"
        />
      )
    }
    return (
      <T
        _str="An account balance payment has been added and paid for."
        _context="Customer account balance banner text"
      />
    )
  }

  const getBannerLink = () => {
    return hasAccountBalancePayment() ? (
      <T
        _str="Edit or remove"
        _context="Customer account balance banner modal link"
      />
    ) : (
      <T
        _str="Add a payment"
        _context="Customer account balance banner modal link"
      />
    )
  }
  const openAccountBalanceModal = () => {
    setShowAccountBalanceModal(true)
  }
  const closeAccountBalanceModal = () => setShowAccountBalanceModal(false)

  const openAddCustomerModal = (query?: string) => {
    if (query !== undefined) setCustomerSearchQuery(query)
    setShowAddCustomerModal(true)
  }
  const closeAddCustomerModal = () => setShowAddCustomerModal(false)

  const handleCustomerSelect = (customer?: ApiCustomer) => {
    if (!customer) {
      return
    }

    Promise.resolve(onSelect(customer))
      .then(() => {
        trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Customer Added to Sale', {
          customerId: customer.id,
          saleId,
          ...customerPaymentMetadata,
        })
      })
      .catch(() => undefined)
  }

  const handleCustomerRemoved = () => {
    trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Customer Removed from Sale', {
      saleId,
      ...customerPaymentMetadata,
    })
  }

  return (
    <>
      <div
        id="wr-payment-add-customer"
        className={'vd-mt5 payment-add-customer customer-card'}
      >
        {!validCustomer && (
          <div className="vd-flex">
            <AddCustomerPopover />
            <div style={{ width: '100%' }}>
              <CustomerSearch
                onAdd={openAddCustomerModal}
                onSelect={handleCustomerSelect}
                onFetch={(query: string) => setCustomerSearchQuery(query)}
                placeholder={getAddCustomerPlaceholder()}
              ></CustomerSearch>
            </div>
          </div>
        )}
        {validCustomer && (
          <Card className="wr-customer-badge">
            {showAccountBalancePaymentBanner() && (
              <div className="vd-banner vd-banner--info wr-customer-badge-banner">
                <div className="vd-flex vd-flex--align-center">
                  <Text type="supplementary">
                    <Icon icon="fa-info-circle" className="vd-banner-icon" />
                    {getBannerText()}
                    <>
                      &nbsp;
                      <Button
                        link
                        className="vd-link vd-link--secondary"
                        onClick={openAccountBalanceModal}
                        data-track="open-account-balance-modal"
                      >
                        {getBannerLink()}
                      </Button>
                    </>
                  </Text>
                </div>
              </div>
            )}
            <div
              className={
                'wr-customer-badge-content vd-pa2 vd-flex vd-flex-row vd-flex--justify-between'
              }
            >
              <CustomerBadge
                size="small"
                apiCustomer={saleCustomer}
                group={customerGroup}
              ></CustomerBadge>
              <RemoveCustomerButton onCustomerRemoved={handleCustomerRemoved} />
              <LockedCustomerIcon />
            </div>
            <CustomerBalancesSection />
          </Card>
        )}
      </div>
      {showAccountBalanceModal && (
        <AccountBalanceSetupModal close={closeAccountBalanceModal} />
      )}
      {showAddCustomerModal && (
        <UpdateSaleCustomerModal
          onClose={closeAddCustomerModal}
          customerSearchQuery={customerSearchQuery}
        />
      )}
    </>
  )
}
